import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Navbar,
  Footer,
  FloatingActions,
  ContactForm,
  SocialRow,
} from "../components/Shared";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.13 } },
};

export default function MobileLanding() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ========================= */}
      {/* TOP BRANDING SECTION */}
      {/* ========================= */}
      <section className="relative pt-6 md:pt-8 pb-4 md:pb-5 bg-white overflow-hidden">
        {/* SOFT GLOW */}
        <div className="absolute top-[-150px] left-[-100px] w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] bg-cyan-200/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <motion.div
            className="flex flex-col items-center justify-center text-center"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {/* BIG LOGO */}
            <motion.img
              variants={fadeUp}
              src="/logo.png"
              alt="BAJABAR Logo"
              className="h-32 sm:h-44 md:h-64 lg:h-72 w-auto object-contain drop-shadow-2xl"
              style={{ scale: 1.05 }}
            />

            {/* COMPANY NAME */}
            <motion.h1
              variants={fadeUp}
              className="mt-2 text-[24px] sm:text-[36px] md:text-[54px] lg:text-[64px] font-black tracking-[-1px] md:tracking-[-2px] text-slate-900 leading-tight px-2"
            >
              BAJABAR INCORPORATION FZE
            </motion.h1>

            {/* TAGLINE */}
            <motion.div
              variants={fadeUp}
              className="mt-4 inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 px-5 sm:px-8 py-4 rounded-[24px] bg-white border border-slate-200 shadow-[0_15px_50px_rgba(0,0,0,0.06)] mx-4"
            >
              {/* ICON */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl flex-shrink-0">
                <i className="fas fa-shield-alt text-lg sm:text-xl md:text-2xl" />
              </div>

              {/* TEXT */}
              <div className="text-center sm:text-left">
                <div className="text-[10px] sm:text-[11px] md:text-sm uppercase tracking-[0.25em] text-slate-500 font-black">
                  Your Trusted Partner
                </div>
                <div className="mt-1 text-[18px] sm:text-[24px] md:text-[32px] font-black leading-tight">
                  <span className="text-slate-900">Global Business Solutions</span>
                  <span className="mx-2 text-blue-700">in</span>
                  <span className="bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">UAE</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* DIVISIONS SECTION */}
      {/* ========================= */}
      <section className="relative pt-4 md:pt-5 pb-10 md:pb-12 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          {/* CARDS */}
          <motion.div
            className="grid sm:grid-cols-2 gap-5 lg:gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            {/* ========================= */}
            {/* LOGISTICS CARD */}
            {/* ========================= */}
            <motion.div
              variants={fadeUp}
              className="group bg-white rounded-[28px] sm:rounded-[32px] overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(0,0,0,0.06)] active:scale-[0.98] hover:-translate-y-2 transition-all duration-500"
            >
              {/* IMAGE */}
              <div className="relative h-40 sm:h-44 md:h-52 overflow-hidden">
                <img
                  src="/backgroundlanding.png"
                  alt="Logistics"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-slate-900/10 to-transparent" />
                <div className="absolute top-4 left-4 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/95 backdrop-blur-xl shadow-lg text-blue-700 font-bold text-xs sm:text-sm">
                  Freight Division
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-5 md:p-6">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-base sm:text-lg shadow-xl">
                  <i className="fas fa-shipping-fast" />
                </div>

                <h3 className="mt-3 sm:mt-4 text-[26px] sm:text-[30px] md:text-[38px] font-black text-slate-900 leading-none">
                  Freight & Logistics
                </h3>

                <p className="mt-3 sm:mt-4 text-slate-600 leading-[1.8] text-[14px] sm:text-[15px] md:text-[17px]">
                  International freight forwarding, customs clearance and end-to-end logistics services worldwide.
                </p>

                <div className="mt-4 sm:mt-5 space-y-2 sm:space-y-3">
                  {["Customs Clearance", "Air, Sea & Land Freight Services", "15+ Years of Industry Experience"].map((item) => (
                    <div key={item} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px] flex-shrink-0">
                        <i className="fas fa-check" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm md:text-base">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/logistics"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 mt-5 sm:mt-6 w-full px-5 sm:px-6 py-3.5 sm:py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold active:scale-95 hover:scale-[1.02] transition-all duration-300 shadow-xl text-sm md:text-base"
                >
                  Explore Logistics
                  <i className="fas fa-arrow-right" />
                </Link>
              </div>
            </motion.div>

            {/* ========================= */}
            {/* TRADING CARD */}
            {/* ========================= */}
            <motion.div
              variants={fadeUp}
              className="group bg-white rounded-[28px] sm:rounded-[32px] overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(0,0,0,0.06)] active:scale-[0.98] hover:-translate-y-2 transition-all duration-500"
            >
              {/* IMAGE */}
              <div className="relative h-40 sm:h-44 md:h-52 overflow-hidden">
                <img
                  src="/tradingbackground.png"
                  alt="Trading"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-slate-900/10 to-transparent" />
                <div className="absolute top-4 left-4 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/95 backdrop-blur-xl shadow-lg text-blue-700 font-bold text-xs sm:text-sm">
                  Trading Division
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-5 md:p-6">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-base sm:text-lg shadow-xl">
                  <i className="fas fa-bolt" />
                </div>

                <h3 className="mt-3 sm:mt-4 text-[26px] sm:text-[30px] md:text-[38px] font-black text-slate-900 leading-none">
                  General Trading
                </h3>

                <p className="mt-3 sm:mt-4 text-slate-600 leading-[1.8] text-[14px] sm:text-[15px] md:text-[17px]">
                  Reliable supply house for electrical, telecom, lighting and industrial power solutions.
                </p>

                <div className="mt-4 sm:mt-5 space-y-2 sm:space-y-3">
                  {["Electrical Components", "Telecom Infrastructure", "Power & UPS Systems"].map((item) => (
                    <div key={item} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px] flex-shrink-0">
                        <i className="fas fa-check" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm md:text-base">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/trading"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 mt-5 sm:mt-6 w-full px-5 sm:px-6 py-3.5 sm:py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold active:scale-95 hover:scale-[1.02] transition-all duration-300 shadow-xl text-sm md:text-base"
                >
                  Explore Trading
                  <i className="fas fa-arrow-right" />
                </Link>
              </div>
            </motion.div>
          </motion.div>

          {/* QUICK STATS */}
          <motion.div
            className="grid grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-3xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[["15+", "Years Experience"], ["500+", "Global Clients"], ["100K+", "Shipments"]].map(([n, l]) => (
              <motion.div
                key={l}
                variants={fadeUp}
                className="bg-white border border-slate-100 rounded-2xl sm:rounded-3xl py-4 sm:py-5 text-center shadow-lg active:scale-95 transition-transform"
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                  {n}
                </div>
                <div className="mt-1 sm:mt-2 text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-slate-500 font-bold px-1">
                  {l}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* HERO SECTION */}
      {/* ========================= */}
      <section
        className="relative min-h-[85vh] sm:min-h-[88vh] overflow-hidden bg-cover bg-center flex items-center"
        style={{ backgroundImage: "url('/backgroundforall.png')" }}
      >
        {/* OVERLAY */}
        <div className="absolute inset-0 bg-white/75 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-white via-white/85 to-transparent" />
        <div className="absolute top-[-200px] left-[-150px] w-[700px] h-[700px] bg-blue-300/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-[-200px] right-[-100px] w-[600px] h-[600px] bg-cyan-200/20 rounded-full blur-[140px] pointer-events-none" />

        {/* CONTENT */}
        <motion.div
          className="relative z-20 max-w-6xl mx-auto px-4 sm:px-5 md:px-6 py-10 sm:py-8 lg:py-12 w-full"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <div className="max-w-4xl mx-auto text-center">
            {/* TITLE */}
            <motion.h1
              variants={fadeUp}
              className="mt-4 sm:mt-8 font-black leading-[1] tracking-[-1px] text-slate-950 text-[30px] sm:text-[40px] md:text-[56px] lg:text-[64px]"
            >
              Global Trading
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                & Logistics
              </span>
              <span className="block text-slate-900">Solutions</span>
            </motion.h1>

            {/* DESCRIPTION */}
            <motion.p
              variants={fadeUp}
              className="mt-5 sm:mt-7 text-sm sm:text-base md:text-[19px] text-slate-600 leading-[1.8] max-w-2xl mx-auto font-medium px-2"
            >
              Trusted freight forwarding, customs clearance, electrical supply and global trading solutions from SAIF Zone, Sharjah, UAE.
            </motion.p>

            {/* BUTTONS */}
            <motion.div
              variants={fadeUp}
              className="mt-7 sm:mt-9 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4 sm:px-0"
            >
              <Link
                to="/logistics"
                className="group relative overflow-hidden inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-3.5 sm:py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-blue-600 text-white font-bold shadow-[0_10px_40px_rgba(37,99,235,0.35)] active:scale-95 hover:scale-105 hover:shadow-[0_20px_60px_rgba(37,99,235,0.45)] transition-all duration-500 text-sm md:text-base"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-all duration-500" />
                <i className="fas fa-shipping-fast relative z-10" />
                <span className="relative z-10">Logistics Division</span>
              </Link>

              <Link
                to="/trading"
                className="group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-3.5 sm:py-4 rounded-2xl bg-white/70 backdrop-blur-xl border border-white/70 text-slate-900 font-bold shadow-xl active:scale-95 hover:bg-white hover:scale-105 transition-all duration-500 text-sm md:text-base"
              >
                <i className="fas fa-bolt text-blue-700 group-hover:rotate-12 transition duration-300" />
                Trading Division
              </Link>
            </motion.div>

            {/* STATS */}
            <motion.div
              variants={stagger}
              className="mt-10 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl mx-auto"
            >
              {[["15+", "Years Experience"], ["500+", "Global Clients"], ["100K+", "Shipments"]].map(([n, l]) => (
                <motion.div key={l} variants={fadeUp} className="group">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent group-hover:scale-110 transition duration-300">
                    {n}
                  </div>
                  <div className="mt-1 sm:mt-2 text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.15em] sm:tracking-[0.25em] text-slate-500 font-semibold">
                    {l}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* BOTTOM FADE */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ========================= */}
      {/* CONTACT SECTION */}
      {/* ========================= */}
      <section
        id="contact"
        className="relative py-16 sm:py-24 bg-gradient-to-b from-white to-slate-50 overflow-hidden"
      >
        <div className="absolute top-[-150px] left-[-100px] w-[400px] h-[400px] bg-blue-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-150px] right-[-100px] w-[400px] h-[400px] bg-cyan-200/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-5 md:px-6">
          {/* HEADING */}
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-[10px] sm:text-xs"
            >
              Contact Us
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="mt-5 sm:mt-7 text-[30px] sm:text-[38px] md:text-[52px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
            >
              Send Your
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                Business Inquiry
              </span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="mt-4 sm:mt-6 text-slate-600 text-base sm:text-lg leading-[1.9]"
            >
              Reach out to BAJABAR INCORPORATION FZE for logistics, freight forwarding, industrial supply, telecom and power infrastructure solutions.
            </motion.p>
          </motion.div>

          {/* CONTENT */}
          <div className="mt-10 sm:mt-16 grid lg:grid-cols-2 gap-6 sm:gap-10">
            {/* LEFT INFO */}
            <motion.div
              className="space-y-4 sm:space-y-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              {/* CARD 1 */}
              <motion.div
                variants={fadeUp}
                className="bg-white rounded-[24px] sm:rounded-[32px] border border-slate-200 p-5 sm:p-7 shadow-[0_15px_45px_rgba(0,0,0,0.06)] active:scale-[0.98] transition-transform"
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl flex-shrink-0">
                    <i className="fas fa-user-tie text-base sm:text-lg" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900">Zubair Bin Mohammed</h3>
                    <div className="mt-1 text-blue-700 font-semibold text-sm sm:text-base">Managing Director</div>
                    <div className="mt-3 text-slate-600 leading-7 text-sm sm:text-base">
                      zubair@bajabarinc.ae<br />+971 525131571
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* CARD 2 */}
              <motion.div
                variants={fadeUp}
                className="bg-white rounded-[24px] sm:rounded-[32px] border border-slate-200 p-5 sm:p-7 shadow-[0_15px_45px_rgba(0,0,0,0.06)] active:scale-[0.98] transition-transform"
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white flex items-center justify-center shadow-xl flex-shrink-0">
                    <i className="fas fa-user-tie text-base sm:text-lg" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900">Mohammed A. Mannan</h3>
                    <div className="mt-1 text-blue-700 font-semibold text-sm sm:text-base">Business Development</div>
                    <div className="mt-3 text-slate-600 leading-7 text-sm sm:text-base">
                      mohammed@bajabarinc.ae<br />+91 81252 11886
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* OFFICE */}
              <motion.div
                variants={fadeUp}
                className="bg-gradient-to-r from-blue-700 to-cyan-500 rounded-[24px] sm:rounded-[32px] p-6 sm:p-8 text-white shadow-[0_20px_60px_rgba(37,99,235,0.3)]"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-map-marker-alt text-lg sm:text-xl" />
                  </div>
                  <div>
                    <div className="uppercase tracking-[0.2em] text-xs font-bold text-blue-100">Office Location</div>
                    <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-black">SAIF Zone, Sharjah, UAE</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT FORM */}
            <motion.div
              className="bg-white rounded-[32px] sm:rounded-[40px] border border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.08)] p-6 sm:p-8 md:p-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900">Get In Touch</h3>
              <p className="mt-2 sm:mt-3 text-slate-600 text-sm sm:text-base">Fill the form and our team will contact you shortly.</p>
              <div className="mt-6 sm:mt-8">
                <ContactForm
                  division="trading"
                  services={["Custom Clearance","Logistics", "Freight Forwarding", "Electrical Supply", "Telecom", "Power Solutions", "Other"]}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================= */}
      {/* MAP SECTION */}
      {/* ========================= */}
      <section className="relative py-16 sm:py-20 bg-white overflow-hidden">
        {/* GLOW */}
        <div className="absolute top-[-120px] left-[-120px] w-[300px] h-[300px] bg-blue-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-120px] right-[-120px] w-[300px] h-[300px] bg-cyan-200/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-5 md:px-6">
          {/* HEADING */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-[10px] sm:text-xs"
            >
              Our Location
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="mt-5 sm:mt-7 text-[30px] sm:text-[38px] md:text-[52px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
            >
              Visit Our
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                UAE Office
              </span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="mt-4 sm:mt-6 text-slate-600 text-base sm:text-lg leading-[1.9]"
            >
              BAJABAR INCORPORATION FZE is located in SAIF Zone, Sharjah, UAE.
            </motion.p>
          </motion.div>

          {/* MAP CARD */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-10 sm:mt-14 rounded-[28px] sm:rounded-[36px] overflow-hidden border border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.08)] bg-white"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3606.3045877990335!2d55.497321500000005!3d25.327558299999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5f8f1e46d7dd%3A0xd03ed01c90ceb51b!2sBAJABAR%20INCORPORATION%20FZE!5e0!3m2!1sen!2sae!4v1779453969057!5m2!1sen!2sae"
              width="100%"
              height="380"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BAJABAR Location"
              className="w-full"
            ></iframe>
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* FOOTER */}
      {/* ========================= */}
      <footer className="relative overflow-hidden bg-gradient-to-br from-[#0a1f57] via-[#0d2c78] to-[#1b3f98] text-white pt-7 pb-4">
        <div className="absolute top-[-150px] right-[-100px] w-[400px] h-[400px] bg-cyan-400/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-6 relative z-10">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 border-b border-white/10 pb-6">
            {/* COMPANY */}
            <div className="flex flex-col items-start">
              <div className="inline-flex items-center justify-center bg-white/95 rounded-2xl px-3 sm:px-4 py-2 shadow-lg">
                <img
                  src="/logo.png"
                  alt="BAJABAR Logo"
                  className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto object-contain drop-shadow-xl hover:scale-105 transition-all duration-500"
                />
              </div>
              <h3 className="mt-3 text-lg sm:text-2xl font-black leading-tight text-white">BAJABAR INCORPORATION FZE</h3>
              <p className="mt-2 sm:mt-3 text-[13px] sm:text-[15px] leading-7 text-blue-100/80 max-w-sm">
                Trusted global trading and logistics partner delivering freight forwarding, customs clearance, telecom, industrial supply and power solutions.
              </p>
            </div>

            {/* QUICK LINKS */}
            <div>
              <h4 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-5">Quick Links</h4>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/logistics" className="text-blue-100/80 hover:text-white transition duration-300 text-sm sm:text-base">
                  Logistics Division
                </Link>
                <Link to="/trading" className="text-blue-100/80 hover:text-white transition duration-300 text-sm sm:text-base">
                  Trading Division
                </Link>
                <Link
                  to="/trading#contact"
                  onClick={() => {
                    setTimeout(() => {
                      const section = document.getElementById("contact");
                      if (section) section.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                  }}
                  className="text-blue-100/80 hover:text-white transition duration-300 text-sm sm:text-base"
                >
                  Contact Us
                </Link>
              </div>
            </div>

            {/* CONTACT */}
            <div>
              <h4 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-5">Contact Info</h4>
              <div className="space-y-4 sm:space-y-5 text-blue-100/85">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-map-marker-alt text-cyan-300 text-sm" />
                  </div>
                  <span className="leading-7 text-sm sm:text-base">SAIF Zone, Sharjah, UAE</span>
                </div>
                <a href="tel:+97165526501" className="flex items-center gap-3 sm:gap-4 hover:text-white transition duration-300">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-phone-alt text-cyan-300 text-sm" />
                  </div>
                  <span className="text-sm sm:text-base">+971 6 5526501</span>
                </a>
                <a href="mailto:sales@bajabarinc.ae" className="flex items-center gap-3 sm:gap-4 hover:text-white transition duration-300">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-envelope text-cyan-300 text-sm" />
                  </div>
                  <span className="text-sm sm:text-base">sales@bajabarinc.ae</span>
                </a>
              </div>
            </div>
          </div>

          {/* BOTTOM */}
          <div className="pt-4 sm:pt-5 flex flex-col md:flex-row items-center justify-between gap-2 sm:gap-3 text-xs sm:text-sm text-blue-100/70 text-center md:text-left">
            <div>© 2026 BAJABAR INCORPORATION FZE. All rights reserved.</div>
            <div>
              Design & Developed by{" "}
              <a
                href="https://crescentitsolutions.com"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-300 hover:text-white font-semibold transition duration-300"
              >
                Crescent IT Solutions INC IL, USA
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}